import EventEmitter from 'eventemitter3';
import styles from './styles.module.scss';
import type { Token } from '../../core/token';
import { deepMerge } from '../../util/data';
import { Option as StandardChartOption, StandardChartIns } from '../../helper/standardChart';

type Config = {
  data: string[];
  token?: Partial<Token>;
  style?: Partial<StandardChartOption['dataZoom']>;
};

type EventTypes = {
  valueRange: (valueRange: number[]) => void;
  dataZoom: (startIndex: number, endIndex: number) => void;
};

export default class CustomDataZoom extends EventEmitter<EventTypes> {
  private __dom!: HTMLElement;

  private __config: Config;

  private _option!: Partial<StandardChartOption>;

  chartIns!: StandardChartIns;

  constructor(dom: HTMLElement, config: Config) {
    super();

    this.__dom = dom;
    this.__dom.classList.add(styles.wrap);
    this.__config = config;

    this.__initialize();
  }

  private __initialize() {
    this.__buildDom();
    this.__buildDataZoom();
    this.__event();
  }

  private __buildDom() {
    const $chart = document.createElement('div');
    $chart.classList.add(styles.chart);
    this.__dom.appendChild($chart);
  }

  private __setDomHeight(height: number) {
    this.__dom.style.height = `${height}px`;
  }

  private __buildDataZoom() {
    const xAxisData = this.__config.data;
    const { token, style = {} } = this.__config;
    const dataZoomData = Array(xAxisData.length).fill(0);
    const chartIns = ThsDataVStandardChart.init(this.__dom.firstChild);
    const defaultToken = {
      type: 'slider',
      show: true,
      bottom: 0,
      height: 16
    };
    // 优先级：用户配置 >> 默认配置 >> 主题配置
    const dataZoomToken = deepMerge(
      deepMerge(token?.dvHXKLine?.dataZoom ?? {}, defaultToken, {
        override: true
      }),
      style,
      {
        override: true
      }
    );
    this.__setDomHeight(dataZoomToken.height as number);

    const option = {
      dataZoom: [dataZoomToken],
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          show: false
        },
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          show: false
        },
        splitLine: {
          show: false
        }
      },
      series: {
        type: 'bar',
        data: dataZoomData
      }
    };
    this._option = option;
    chartIns.play({ option });

    this.chartIns = chartIns;
  }

  private __event() {
    const self = this;
    const { chartIns } = self;
    const xAxisData = self.__config.data;
    let [dataZoomStartIndex, dataZoomEndIndex] = [0, xAxisData.length - 1];
    chartIns.on('dv:afterupdate', () => {
      const valueRange = chartIns
        .getECharts()
        .getModel()
        .getComponent('dataZoom')
        .getValueRange('x', 0);
      self.emit('valueRange', valueRange as number[]);
    });

    chartIns.on('datazoom', event => {
      const startIndex = Math.round((event.start / 100) * (xAxisData.length - 1));
      const endIndex = Math.round((event.end / 100) * (xAxisData.length - 1));

      if (startIndex !== dataZoomStartIndex || endIndex !== dataZoomEndIndex) {
        // 设置行情图的区间
        [dataZoomStartIndex, dataZoomEndIndex] = [startIndex, endIndex];

        self.emit('dataZoom', startIndex, endIndex);
      }
    });
  }

  update(data: string[]) {
    const option = this._option;
    this.__config.data = data;
    option.dataZoom[0].endValue = data.length - 1;
    option.xAxis.data = data;
    option.series.data = Array(data.length).fill(0);
    this.chartIns.play({ option });
    this.__event();
  }

  destroy() {
    this.chartIns.destroy();
    this.__dom.innerHTML = '';
  }
}
